import { useQuery } from '@apollo/client/react';
import clsx from 'clsx';
import { GetServerSideProps, NextPage } from 'next';
import { SSRConfig, useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useCallback, useMemo } from 'react';

import { CandidateDocument } from '@pflegia/data-access/candidate.generated';
import { MapLazyLoad, useMultipleVariantData } from '@pflegia/feature-flags';
import { CareType, Employer, LOCATION_PUBLISHED_STATUSES, PRAXIA_CARE_TYPES } from '@pflegia/types/api/employer';
import { SourceDomain } from '@pflegia/types/api/source-domain/source-domain';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { getButtonClassName } from '@pflegia/ui/shared/button';
import LoadingModal from '@pflegia/ui/shared/dialog/LoadingModal';
import useCandidateLocationForJob from '@pflegia/ui/shared/hooks/use-candidate-location-for-job';
import SectionCard from '@pflegia/ui/shared/section-card/SectionCard';
import { getPflegiaDefaultDomainName } from '@pflegia/ui/shared/utils/get-pflegia-default-domain';
import { shouldImageBeUnoptimized } from '@pflegia/ui/shared/utils/image-unoptimized';
import { useMatchDialogueStore } from '@pflegia/ui/stores/match-dialogue.store';
import { isValidUuidV4 } from '@pflegia/util/common/is-valid-uuid-v4';
import { canHaveGeoArea } from '@pflegia/util/functions/employer';
import { formatNameWithTitles } from '@pflegia/util/functions/format-name';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import { employerTypeTitle } from '@components/position-card/employer-type-mappings';
import ReactPlayerWrapper from '@components/react-player-wrapper/ReactPlayerWrapper.component';

import defaultPerks from '@domains/job-detail/components/job-overview/DefaultJobPerks';
import { getSortedLocationsByDistance } from '@domains/job-detail/utils/get-sorted-locations-by-distance';

import BothCheckingPhoneSmiling from '@public/assets/both-checking-phone-smiling.png';
import Map from '@public/assets/map.png';

import { getApolloClientSsr } from '@shared/application/apollo-ssr-client';
import { careTypeToTypeOfEmployer } from '@shared/application/care-type-to-type-of-employer';
import { HOME_REDIRECT } from '@shared/application/constants';
import NextSeo from '@shared/components/NextSeo';
import { LOCALES } from '@shared/constants/locale';
import { JobDetailsProvider, useJobDetails } from '@shared/context/job-details.context';
import { EMPLOYER_PAGE_QUERY } from '@shared/graphql/operations/employer-page';
import { serverSideTranslations } from '@shared/utils/server-side-translations';

import { getEmployerGalleryMainImage } from '@utils/employer-gallery';
import { getGlobalSsrProps } from '@utils/global-ssr-props';

const Arguments = dynamic(() => import('domains/job-detail/components/arguments/Arguments.component'), { ssr: false });
const MapWithZonesWrapper = dynamic(() => import('@domains/job-detail/components/map-with-zones/MapWithZonesWrapper'), { ssr: false });
const CareTypeWithIcon = dynamic(() => import('@components/care-type-with-icon/CareTypeWithIcon'), { ssr: false });
const DirectionsMapWrapper = dynamic(() => import('@pflegia/ui/shared/map/DirectionsMapWrapper'), { ssr: false });

const DocumentationSystems = dynamic(() => import('domains/job-detail/components/documentation-systems/DocumentationSystems.component'), {
  ssr: false,
});
const EmployeeTestimony = dynamic(() => import('domains/job-detail/components/employee-testimony/EmployeeTestimony.component'), {
  ssr: false,
});
const Gallery = dynamic(() => import('@pflegia/ui/shared/gallery/Gallery'), { ssr: false });
const MobileSection = dynamic(() => import('@components/mobile-section/MobileSection'), { ssr: false });
const OffersDialogueModal = dynamic(() => import('@components/dialogue/OffersDialogueModal'), {
  loading: () => <LoadingModal />,
  ssr: false,
});
const OtherOpenPositions = dynamic(() => import('domains/job-detail/components/other-open-positions/OtherOpenPositions.component'), {
  ssr: false,
});
const SummaryItem = dynamic(() => import('@domains/job-detail/components/job-overview/SummaryItem'), { ssr: false });

type EmployerPageProps = Pick<
  Employer,
  | 'aboutUs'
  | 'beds'
  | 'bestArgumentContent'
  | 'bestArgumentQuote'
  | 'careType'
  | 'displayContactPerson'
  | 'documentationSystems'
  | 'logoKey'
  | 'gallery'
  | 'geoLocation'
  | 'id'
  | 'name'
  | 'overtimeCompensations'
  | 'patients'
  | 'applicationProcess'
  | 'numberOfDoctors'
  | 'status'
  | 'salaryNegotiations'
  | 'sourceDomains'
  | 'teamPhotoKey'
  | 'testimonyAuthor'
  | 'testimonyImageKey'
  | 'testimonyJobtitle'
  | 'testimonyQuote'
  | 'youtubeVideoUrl'
  | 'patientAreas'
>;

const EmployerPageBase: NextPage<EmployerPageProps> = ({
  aboutUs,
  beds,
  bestArgumentContent,
  bestArgumentQuote,
  careType,
  displayContactPerson,
  documentationSystems,
  logoKey,
  applicationProcess,
  numberOfDoctors,
  gallery,
  geoLocation,
  name,
  overtimeCompensations,
  patients,
  salaryNegotiations,
  sourceDomains,
  teamPhotoKey,
  testimonyAuthor,
  testimonyImageKey,
  testimonyJobtitle,
  testimonyQuote,
  youtubeVideoUrl,
  patientAreas,
}) => {
  const { t } = useTranslation(['candidateFields', 'employers', 'jobDetails']);
  const authenticated = useAuth();
  const { data: candidateData } = useQuery(CandidateDocument, { skip: !authenticated });
  const candidate = candidateData?.candidate;
  const { candidateLocation } = useCandidateLocationForJob(candidate?.locations ?? [], geoLocation);
  const typeOfEmployer = careTypeToTypeOfEmployer(careType);
  const overtimeTexts = overtimeCompensations?.map(toTitle);
  const salaryTexts = salaryNegotiations?.map(toTitle);

  const {
    setMatchDetails,
    relatedJobs: { total, loading, jobs, setPage },
  } = useJobDetails();

  const { open: isMatchModalVisible, onOpen } = useMatchDialogueStore();

  const handlePositionCtaClick = useCallback(
    (connectionInfo: ConnectionInfo) => {
      onOpen(connectionInfo);
      setMatchDetails(connectionInfo);
    },
    [onOpen, setMatchDetails],
  );

  const sortedLocations = useMemo(() => {
    const locations = [geoLocation, ...(patientAreas || [])].filter(
      (location): location is NonNullable<typeof location> => location !== null,
    );
    return candidateLocation ? getSortedLocationsByDistance(locations, candidateLocation) : locations;
  }, [candidateLocation, geoLocation, patientAreas]);

  const hasImage = gallery && gallery.length > 0;
  const contactPersonName = formatNameWithTitles(displayContactPerson);
  const ambulantEmployer = careType && careType === CareType.Ambulant;
  const isPraxiaCareType = PRAXIA_CARE_TYPES.includes(careType);
  const { isTest } = useMultipleVariantData(MapLazyLoad.Name);
  const logoUrl = getPublicImageUrl(logoKey);

  return (
    <>
      <NextSeo title={name} flavorDomain={getPflegiaDefaultDomainName(sourceDomains)} nofollow noindex />

      <div className='bg-background-secondary py-6 md:py-16'>
        <div className='container mx-auto px-6'>
          <div className='mb-8 flex flex-col gap-6 md:mb-24 md:gap-8'>
            <div className='grid grid-cols-1 gap-6 md:grid-cols-10 md:rounded-2xl md:bg-white md:px-12 md:py-6'>
              <MobileSection className='flex items-center justify-between md:col-span-full md:border-b md:border-gray-200 md:pb-4'>
                {geoLocation && (
                  <SummaryItem
                    black
                    className='hidden md:block'
                    emoji={defaultPerks.address}
                    label={t('jobDetails:perks.address')}
                    value={geoLocation.formattedAddress}
                  />
                )}
                <CareTypeWithIcon type={careType} />
              </MobileSection>

              <MobileSection className='md:col-span-full md:row-start-1'>
                <h1 className='text-default text-xl font-semibold md:text-4xl'>{name}</h1>
              </MobileSection>

              <MobileSection className='flex flex-col gap-4 md:col-span-6 md:gap-6'>
                <div className={clsx('relative', hasImage && 'h-44 md:h-60')}>
                  {hasImage && (
                    <Image
                      alt=''
                      className='rounded-2xl object-contain'
                      fill
                      sizes='(max-width: 768px) 100vw, 50vw'
                      src={getEmployerGalleryMainImage({ gallery })}
                    />
                  )}
                  {logoUrl && (
                    <Image
                      alt=''
                      className={clsx('rounded-2xl object-contain', hasImage && 'absolute top-4 left-4')}
                      height={80}
                      src={logoUrl}
                      unoptimized={shouldImageBeUnoptimized(logoUrl)}
                      width={60}
                    />
                  )}
                </div>

                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                  {geoLocation && (
                    <SummaryItem
                      black
                      className='md:hidden'
                      emoji={defaultPerks.address}
                      label={t('jobDetails:perks.address')}
                      value={geoLocation.formattedAddress}
                    />
                  )}
                  {careType && (
                    <SummaryItem
                      black
                      className='order-2 md:order-1'
                      emoji='🏥'
                      label={t('employers:typeOfInstitute')}
                      value={t(`candidateFields:typeOfEmployer.${employerTypeTitle(typeOfEmployer)}`)}
                    />
                  )}
                  {salaryTexts && salaryTexts.length > 0 && (
                    <SummaryItem
                      black
                      className='order-3 md:order-3'
                      emoji={defaultPerks.salaryNegotiations}
                      label={t('jobDetails:perks.salaryNegotiations')}
                      value={salaryTexts}
                    />
                  )}
                  {overtimeTexts && overtimeTexts.length > 0 && (
                    <SummaryItem
                      black
                      className='order-4 md:order-2'
                      emoji={defaultPerks.overtimeRegulations}
                      label={t('jobDetails:perks.overtimeRegulations')}
                      value={overtimeTexts}
                    />
                  )}
                  {beds && !ambulantEmployer && !isPraxiaCareType && (
                    <SummaryItem
                      black
                      className='order-5 md:order-4'
                      emoji={defaultPerks.numberOfBeds}
                      label={t('jobDetails:perks.numberOfBeds')}
                      value={beds}
                    />
                  )}
                  {applicationProcess && isPraxiaCareType && (
                    <SummaryItem
                      black
                      className='order-5 md:order-4'
                      emoji={defaultPerks.applicationProcess}
                      label={t('jobDetails:perks.applicationProcess')}
                      value={applicationProcess}
                    />
                  )}
                  {numberOfDoctors && isPraxiaCareType && (
                    <SummaryItem
                      black
                      className='order-5 md:order-4'
                      emoji={defaultPerks.numberOfDoctors}
                      label={t('jobDetails:perks.numberOfDoctors')}
                      value={numberOfDoctors}
                    />
                  )}
                  {(patients || beds) && (ambulantEmployer || isPraxiaCareType) && (
                    <SummaryItem
                      black
                      className='order-5 md:order-4'
                      emoji={defaultPerks.numberOfPatients}
                      label={t('jobDetails:perks.numberOfPatients')}
                      value={(patients || beds) as string}
                    />
                  )}
                </div>
              </MobileSection>

              <MobileSection className='md:col-span-4 md:border-l md:pl-8'>
                <h2 className='font-handwriting text-main-blue mb-4 text-xl md:mb-6 md:text-3xl'>{t('jobDetails:contactPerson.title')}</h2>
                {displayContactPerson?.pictureKey && (
                  <div className='relative mb-4 h-44 md:mb-6'>
                    <Image
                      alt=''
                      className='rounded-2xl object-contain'
                      fill
                      sizes='(max-width: 768px) 100vw, 33vw'
                      src={getPublicImageUrl(displayContactPerson.pictureKey) as string}
                    />
                  </div>
                )}
                <div className='mb-4 text-center md:mb-6 md:text-left'>
                  {contactPersonName && <p className='text-default md:text-main-blue text-lg font-semibold'>{contactPersonName}</p>}
                  {displayContactPerson?.jobTitle && <p className='text-default'>{displayContactPerson.jobTitle}</p>}
                </div>
                {!loading && jobs?.length > 0 && (
                  <>
                    <p className='md:text-secondary hidden md:mb-6 md:block md:text-sm'>
                      {t('employers:contactPerson.descriptionDesktop')}
                    </p>
                    <a className={getButtonClassName('apply', 'w-full md:w-auto')} href='#positions'>
                      {t('employers:contactPerson.cta')}
                    </a>
                    <p className='text-secondary mt-4 text-center text-sm md:hidden'>{t('employers:contactPerson.descriptionMobile')}</p>
                  </>
                )}
              </MobileSection>
            </div>

            {geoLocation && patientAreas && patientAreas.length > 0 && canHaveGeoArea(careType) && (
              <MapWithZonesWrapper locations={sortedLocations} centralLocationId={geoLocation.id} candidateLocation={candidateLocation} />
            )}

            {geoLocation && (!patientAreas || patientAreas.length === 0 || !canHaveGeoArea(careType)) && (
              <DirectionsMapWrapper
                anonymous={false}
                jobLocation={geoLocation}
                mapPlaceholder={Map}
                leftDecorativeImage={BothCheckingPhoneSmiling}
                candidateLocation={candidateLocation}
                initiallyVisible={isTest}
              />
            )}

            {aboutUs && (
              <SectionCard titleI18nKey='jobDetails:aboutUs.title'>
                <div className='text-default' dangerouslySetInnerHTML={{ __html: aboutUs }} />
              </SectionCard>
            )}

            {documentationSystems && documentationSystems.length > 0 && (
              <DocumentationSystems anonymous={false} careType={careType} documentationSystems={documentationSystems} />
            )}

            {(bestArgumentContent || bestArgumentQuote) && (
              <Arguments anonymous={false} author={name} bestArgument={bestArgumentContent} bestArgumentQuote={bestArgumentQuote} />
            )}

            {gallery && gallery.length > 0 && <Gallery anonymous={false} images={gallery} />}

            {testimonyAuthor && testimonyJobtitle && testimonyQuote && (
              <EmployeeTestimony
                anonymous={false}
                author={testimonyAuthor}
                employeeTestimonyPhoto={getPublicImageUrl(testimonyImageKey)}
                jobTitle={testimonyJobtitle}
                quote={testimonyQuote}
              />
            )}

            {youtubeVideoUrl && (
              <SectionCard titleI18nKey='jobDetails:insights.title'>
                <ReactPlayerWrapper url={youtubeVideoUrl} />
              </SectionCard>
            )}

            {teamPhotoKey && (
              // TODO-WM: reuse OurTeam component but it need some refactor
              <SectionCard titleI18nKey='jobDetails:ourTeam.title'>
                <div className='our-team-image-container flex w-full flex-col items-center'>
                  <Image
                    alt=''
                    id='our-team-image'
                    className='static! mx-auto! my-0! w-auto! rounded-2xl object-contain'
                    src={getPublicImageUrl(teamPhotoKey) || ''}
                    fill
                    sizes='(max-width: 1024px) 100vw, 1024px'
                  />
                </div>
              </SectionCard>
            )}
          </div>
          <OtherOpenPositions
            openDialog={handlePositionCtaClick}
            titleKey='employers:openPositions.title'
            total={total}
            loading={loading}
            jobs={jobs}
            setPage={setPage}
          />
        </div>
        {isMatchModalVisible && <OffersDialogueModal />}
      </div>
    </>
  );
};

const toTitle = (input: { title: string }) => input.title;

export const getServerSideProps: GetServerSideProps<EmployerPageProps & SSRConfig, { id: string }> = async ({
  locale = LOCALES[0],
  params,
  req,
  query,
}) => {
  const id = params?.id;
  if (!id || !isValidUuidV4(id)) return { notFound: true };

  const translations = await serverSideTranslations(locale, ['common', 'candidateFields', 'employers', 'job', 'jobDetails', 'funnel']);
  const apolloClient = getApolloClientSsr(req, locale);

  try {
    const result = await apolloClient.query<{ employer: EmployerPageProps }, { id: string }>({
      fetchPolicy: 'no-cache',
      query: EMPLOYER_PAGE_QUERY,
      variables: { id },
    });

    if (result.error || !result.data) throw result.error || new Error('No data');
    const { employer } = result.data;
    if (employer.sourceDomains?.length === 1 && employer.sourceDomains[0] === SourceDomain.DociaDe) {
      return { notFound: true };
    }
    if (query.preview === 'true' || LOCATION_PUBLISHED_STATUSES.includes(employer.status)) {
      return {
        props: {
          ...getGlobalSsrProps(req),
          ...translations,
          ...employer,
        },
      };
    }

    return HOME_REDIRECT;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return { notFound: true };
  }
};

const EmployerPage: React.FC<EmployerPageProps> = props => {
  const { id } = props;
  return (
    <JobDetailsProvider employerId={id} populateEmployerRelatedJobs>
      <EmployerPageBase {...props} />
    </JobDetailsProvider>
  );
};
export default EmployerPage;
